/* ------------------------------------------------------------------ *
 * YouTube launch videos: reading the video id from a link, clip times
 * typed as "29:41", and the embed address for playing just that part.
 * ------------------------------------------------------------------ */

// The 11-character video id from any usual YouTube link
// (watch?v=, youtu.be/, embed/, shorts/, live/); null if there is none.
export function youTubeId(url: string): string | null {
  const match = url
    .trim()
    .match(/^https:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

// "29:41" -> 1781, "1:02:03" -> 3723, "95" -> 95; null if it isn't a time.
export function parseClipTime(text: string): number | null {
  const t = text.trim();
  if (!/^\d+(:\d{1,2}){0,2}$/.test(t)) return null;
  return t.split(":").reduce((sum, part) => sum * 60 + Number(part), 0);
}

// 1781 -> "29:41", 3723 -> "1:02:03"
export function formatClipTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const pad = (n: number) => String(n).padStart(2, "0");
  const h = Math.floor(s / 3600);
  return h > 0 ? `${h}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}` : `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

// Embed address (privacy-enhanced youtube-nocookie.com) that plays from
// `startS` and stops at `endS`. Starts muted: browsers only allow sound
// after a click, and the group can unmute in the player.
export function youTubeEmbedUrl(id: string, startS: number, endS: number | null): string {
  const params = new URLSearchParams({
    start: String(Math.floor(startS)),
    autoplay: "1",
    mute: "1",
    controls: "1",
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
  });
  if (endS !== null) params.set("end", String(Math.ceil(endS)));
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}
